import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReaderStore } from '../../stores/reader';
import { ReaderToolbar } from './ReaderToolbar';
import { ReadingSettings } from './ReadingSettings';
import { ModeRenderer } from './ModeRenderer';
import { TranslateAllButton } from './TranslateAllButton';
import { GuideChat } from './GuideChat';
import { TtsPlayer } from './TtsPlayer';
import { AnnotationEditor } from './AnnotationEditor';
import { SelectionTranslate } from './SelectionTranslate';
import { Button } from '@hump/ui';

export function ReaderWorkspace({ docId }: { docId: string }) {
  const { t } = useTranslation();
  const { blocks, loading, error, displayMode, loadDoc, loadMore, hasMore, loadingMore, updateProgress, settings, setGuideOpen } = useReaderStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // #4 节流：避免 IntersectionObserver 每次可见都写库。用 ref 记录上次调用时间。
  // 2 秒 throttle——滑动时最多每 2 秒写一次进度。
  const lastProgressRef = useRef<number>(0);

  useEffect(() => {
    loadDoc(docId);
  }, [docId, loadDoc]);

  // 追踪阅读进度——用 ref 限定查询范围，加微延迟确保 DOM 渲染
  useEffect(() => {
    if (blocks.length === 0 || !contentRef.current) return;

    const timer = setTimeout(() => {
      const container = contentRef.current;
      if (!container) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            const blockId = visible[0].target.getAttribute('data-block-id');
            if (blockId) {
              // #4 throttle：2 秒内已写过则跳过，避免 IntersectionObserver 频繁写库
              const now = Date.now();
              if (now - lastProgressRef.current < 2000) return;
              lastProgressRef.current = now;
              updateProgress(blockId);
            }
          }
        },
        { rootMargin: '-20px 0px -60% 0px', threshold: 0.1 }
      );

      const elements = container.querySelectorAll('[data-block-id]');
      elements.forEach((el) => observer.observe(el));

      // 清理函数存在闭包中
      (container as unknown as { _observer?: IntersectionObserver })._observer = observer;
    }, 100);

    return () => {
      clearTimeout(timer);
      const container = contentRef.current;
      const obs = (container as unknown as { _observer?: IntersectionObserver })?._observer;
      obs?.disconnect();
    };
  }, [blocks, updateProgress]);

  // 无限滚动：sentinel 进入视口时加载更多
  useEffect(() => {
    if (!hasMore || loadingMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-charcoal/50">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-error">{t('reader.notFound')}</p>
        <Button variant="ghost" onClick={() => loadDoc(docId)}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      settings.theme === 'sepia'
        ? 'bg-[#F5E6D3] text-[#3E2C1A]'
        : settings.theme === 'dark'
          ? 'bg-warm-dark'
          : 'bg-warm-white'
    }`}>
      <ReaderToolbar />
      <div ref={contentRef} className="max-w-5xl mx-auto px-4 py-4">
        {blocks.length === 0 ? (
          <p className="text-charcoal/50 text-center py-20">{t('reader.empty')}</p>
        ) : (
          <>
            <div className="mb-3">
              <TranslateAllButton />
            </div>
            <ModeRenderer blocks={blocks} mode={displayMode} docId={docId} onAskAi={() => setGuideOpen(true)} />
            {/* 无限滚动 sentinel */}
            {hasMore && (
              <div ref={sentinelRef} className="py-8 text-center">
                {loadingMore ? (
                  <p className="text-sm text-charcoal/40">加载更多...</p>
                ) : (
                  <button
                    onClick={() => loadMore()}
                    className="text-sm text-camel hover:text-camel-dark"
                  >
                    加载更多 ↓
                  </button>
                )}
              </div>
            )}
            {!hasMore && blocks.length > 0 && (
              <p className="text-xs text-charcoal/30 text-center py-4">— 已到文末 —</p>
            )}
          </>
        )}
      </div>
      <ReadingSettings />
      <TtsPlayer />
      <AnnotationEditor />
      <SelectionTranslate docId={docId} />
      <GuideChat docId={docId} />
    </div>
  );
}
